// Thin fetch wrapper for the UIF Site API.
// In production, VITE_API_BASE_URL is set in .env.production.
// When served via CloudFront, /api/* is proxied to the API Gateway origin,
// so the relative path "/api/..." works without a configured base URL.

const BASE_URL: string = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

function url(path: string): string {
  if (BASE_URL) return `${BASE_URL}${path}`;
  return path; // same-origin (works behind CloudFront)
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(url(path), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
  const data = await res.json().catch(() => ({} as any));
  if (!res.ok) {
    const err = data?.error;
    const message =
      (typeof err === 'object' && err && (err.message || err.code)) ||
      (typeof err === 'string' && err) ||
      data?.message ||
      `HTTP ${res.status}`;
    throw new Error(String(message));
  }
  return data as T;
}

// ----------------------------------------------------------------------------
// Shared types
// ----------------------------------------------------------------------------

export type Role = 'uif' | 'amp' | 'agc';

// Task #15: event format dimensions
export type EventFormat = 'in_person' | 'online' | 'hybrid';
export type OnlineProvider = 'zoom' | 'teams' | 'meet' | 'webex' | 'other';
export type OnlineUrlVisibility = 'public' | 'registered_only' | 'farmer_signed_in';
export type AttendanceMode = 'in_person' | 'online';
export type Audience = 'public' | 'amp' | 'agc' | 'internal';
export type BookingStatus = 'proposed' | 'confirmed' | 'blackout' | 'cancelled';
export type BookingSource = 'uif' | 'agc';
export type FundingSource = 'LFPP' | 'AMP' | 'UIF';
export type TagCategory = 'topic' | 'program';
// Phase 3d-1e: provisions picklist values (matches DB CHECK constraint).
export type Provision = 'breakfast' | 'lunch' | 'dinner' | 'snacks';

export interface EventTag {
  tag_id: number;
  tag_name: string;
  tag_category: TagCategory;
  display_order: number;
  description?: string | null;
}

// Existing public event shape (now includes audience from migration 014).
// hero_photo_url and video_embed_url are placeholders for fields the backend
// doesn't expose yet (Phase 3e backend work will populate them from
// cal_core_entry.json_config or new dedicated columns). Frontend uses them
// conditionally — events without these set don't render those sections.
export interface PublicEvent {
  event_id: number;
  calendar_entry_id: number;
  global_id: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  location?: string;
  venue_details?: string;
  event_type?: string;
  registration_deadline?: string;
  materials?: string;
  max_attendees?: number;
  current_count?: number;
  waitlist_enabled?: boolean;
  waitlist_count?: number;
  availability_status?: 'open' | 'waitlist' | 'closed';
  status?: string;
  audience?: Audience;
  slug?: string | null;               // Phase 027: persistent URL slug used at /#e/<slug>
  hero_photo_url?: string | null;     // path or URL to event photo (e.g. /event-photos/food-safety.jpg)
  video_embed_url?: string | null;    // YouTube embed URL (https://www.youtube.com/embed/<id>)
  cost_text?: string | null;          // Phase 3d-1d: replaces hardcoded "Free" on detail page
  stipend_text?: string | null;       // Phase 3d-1d: optional stipend / honorarium info
  doors_open_text?: string | null;    // Phase 3d-1d: replaces hardcoded "8:00am — coffee & registration"
  provisions?: Provision[] | null;    // Phase 3d-1e: meals/refreshments included (replaces hardcoded "Lunch provided")
  agenda_md?: string | null;          // Phase 3d-1c: per-event agenda modal (markdown)
  about_md?: string | null;           // Phase 3d-1c: per-event "about" modal (markdown)
  presenters_md?: string | null;      // Phase 3d-1c: per-event presenters modal (markdown)
  resource_id?: number | null;        // Phase 023: linked UIF booking's resource (for edit prefill)
  staff_resource_ids?: number[];      // Task #3: assigned staff resources (resource_type='staff')
  // Task #15: online + hybrid event delivery.
  format?: EventFormat;
  online_provider?: OnlineProvider | null;
  online_meeting_url?: string | null;          // redacted unless visibility=public or caller is admin
  online_meeting_id?: string | null;
  online_meeting_passcode?: string | null;
  online_join_instructions?: string | null;
  online_url_visibility?: OnlineUrlVisibility;
  online_max_attendees?: number | null;
  online_current_count?: number;
  // Phase 026 series + prereq fields
  series_id?: number | null;
  series_label?: string | null;
  series_type?: SeriesType | null;
  recurrence_pattern?: RecurrencePattern | null;
  sequence_number?: number | null;
  instance_label?: string | null;
  prereq_text?: string | null;
  prereq_event_ids?: number[];
  days?: EventDay[];                  // Phase 028: Multi-Day Events per-day rows
  // Round 3: Recurring Event template fields
  is_recurring_template?: boolean;
  parent_template_id?: number | null;
  instance_notes?: string | null;
  // Round 4: Draft mode. New events default to draft; Go Live publishes.
  is_draft?: boolean;
  tags?: string[];                    // filter system (Food Safety, AMP, etc.)
}

// Phase 3d-1h: presenter library row.
export interface Presenter {
  presenter_id: number;
  name: string;
  role?: string | null;
  photo_url?: string | null;
  bio?: string | null;
  display_order: number;
}

export interface PresenterInput {
  name: string;
  role?: string | null;
  photo_url?: string | null;
  bio?: string | null;
  display_order?: number | null;
}

export type PresenterUpdateInput = Partial<PresenterInput>;

// Phase 3d-1i: location library row.
export interface Location {
  location_id: number;
  name: string;
  address?: string | null;
  venue_details?: string | null;
  url?: string | null;
  photo_url?: string | null;
  display_order: number;
}

export interface LocationInput {
  name: string;
  address?: string | null;
  venue_details?: string | null;
  url?: string | null;
  photo_url?: string | null;
  display_order?: number | null;
}

export type LocationUpdateInput = Partial<LocationInput>;

// Phase 026: series + recurrence
export type SeriesType = 'multi_day' | 'weekly' | 'monthly' | 'custom';
export type RecurrenceFreq = 'daily' | 'weekly' | 'monthly';
export type Weekday = 'SU' | 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA';

export interface RecurrencePattern {
  freq: RecurrenceFreq;
  interval: number;
  byday?: Weekday[];
  count?: number | null;
  until?: string | null;
  // Task #36: positional weekday for monthly recurrence.
  //   {freq:'monthly', byday:['MO'], bysetpos: 1} = first Monday of each month
  //   {freq:'monthly', byday:['FR'], bysetpos: -1} = last Friday of each month
  // Valid values: 1, 2, 3, 4, -1.
  bysetpos?: number | null;
}

export interface Series {
  series_id: number;
  series_label: string;
  series_type: SeriesType;
  recurrence_pattern?: RecurrencePattern | null;
}

export interface SeriesInput {
  series_label: string;
  series_type: SeriesType;
  recurrence_pattern?: RecurrencePattern | null;
}

export type SeriesUpdateInput = Partial<SeriesInput>;

// Phase 028: Multi-Day Events — per-day detail row.
// Override fields are null when the day inherits from the event-level field.
export interface EventDay {
  day_id?: number;
  day_index: number;
  day_date: string;           // YYYY-MM-DD
  start_time?: string | null; // HH:MM:SS
  end_time?: string | null;
  doors_open_text?: string | null;
  buffer_before_minutes?: number | null;
  buffer_after_minutes?: number | null;
  provisions?: Provision[] | null;
  agenda_md?: string | null;
  notes?: string | null;
}

export interface RegistrationInput {
  event_id: number;
  full_name: string;
  email: string;
  phone?: string;
  attendee_count: number;
  special_requests?: string;
  // Task #50: optional guest names. Length should be <= attendee_count - 1;
  // server truncates extras silently.
  guest_names?: string[];
  // Task #15: required when registering for a hybrid event; ignored otherwise.
  attendance_mode?: AttendanceMode;
}

export interface RegistrationResult {
  status: 'registered' | 'waitlisted' | 'closed' | 'duplicate';
  attendee_id?: number;
  position_in_queue?: number;
  message?: string;
}

export interface AdminLoginInput {
  username: string;
  password: string;
}

export interface AuthUser {
  admin_user_id: number;
  username: string;
  display_name: string | null;
  email: string;
  role: Role;
}

export interface AdminLoginResult {
  token: string;
  expires_at: string;
  user: AuthUser;
}

export interface RegistrationGuest {
  guest_id: number;
  full_name: string;
}

export interface AdminRegistration {
  attendee_id: number;
  event_id: number;
  event_title: string;
  full_name: string;
  email: string;
  phone: string | null;
  attendee_count: number;
  registration_status: string;
  payment_status: string;
  created_at: string;
  special_requests?: string | null;
  // Task #48: attendance confirmation. Stamped when the registrant clicks
  // the confirm link in an admin-sent email. Null until they confirm.
  confirmed_at?: string | null;
  // Task #50: additional named attendees under this registration. Empty
  // array (never null) when the registration is for a single seat.
  guests?: RegistrationGuest[];
}

// Task #48: email blast types. Enhanced filter values + attendee_ids
// override added per task #1 (email enhancements).
export type EmailRecipientFilter =
  | 'registered'
  | 'registered_unconfirmed'
  | 'registered_confirmed'
  | 'registered_and_waitlisted'
  | 'all_active';

export interface EmailBlastInput {
  subject: string;
  body_md: string;
  recipient_filter: EmailRecipientFilter;
  require_confirmation: boolean;
  // When non-empty, overrides recipient_filter — sends only to these
  // specific attendees. Used by "Email selected (N)" in the admin.
  attendee_ids?: number[];
}

export interface EmailBlastResult {
  sent: number;
  failed: number;
  total: number;
  failures: Array<{ to: string; error: string }>;
}

export interface ConfirmRegistrationResult {
  full_name: string;
  event_title: string;
  start_date: string;
  end_date: string;
  location: string | null;
  already_confirmed: boolean;
}

// Task #49: per-day attendance. Task #50: optional guest_id.
export interface AttendanceRow {
  attendance_id: number;
  attendee_id: number;
  guest_id: number | null;
  day_index: number;
  present: boolean;
  check_in_time: string | null;
  check_in_method: string | null;
  signature_url: string | null;
  signature_typed: string | null;
  notes: string | null;
  marked_at: string;
}

// Task #44: printable attendance report. A guest carries its own attendance
// and its own signature — never a name list under the registrant.
export interface AttendanceReportSignatory {
  marked: boolean;                 // false = never marked, distinct from present=false
  present: boolean | null;         // null when !marked
  check_in_time: string | null;
  check_in_method: string | null;
  signature_url: string | null;    // base64 data-URL
  signature_typed: string | null;
}

export interface AttendanceReportGuest extends AttendanceReportSignatory {
  guest_id: number;
  full_name: string | null;
}

export interface AttendanceReportAttendee extends AttendanceReportSignatory {
  attendee_id: number;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  registration_status: string | null;
  address: Record<string, string> | null;   // Phase B; null until then
  address_collected_at: string | null;      // Phase B; null until then
  stipend_flag: boolean;
  notes: string | null;
  group: 'signed' | 'manual' | 'absent' | 'not_marked';
  guests: AttendanceReportGuest[];
}

export interface AttendanceReportDay {
  day_index: number;
  day_date: string | null;
  start_time: string | null;
  end_time: string | null;
  attendees: AttendanceReportAttendee[];
}

export interface AttendanceReport {
  event: {
    event_id: number;
    calendar_entry_id: number;
    title: string;
    start_date: string;
    end_date: string | null;
    day_count: number;
  };
  generated_at: string;
  days: AttendanceReportDay[];
}

export interface AttendanceMarkInput {
  day_index: number;
  present: boolean;
  guest_id?: number | null;
  check_in_method?: 'manual' | 'qr_self' | 'email_link' | 'signature_drawn' | 'signature_typed' | 'walk_in';
  signature_url?: string | null;
  signature_typed?: string | null;
  notes?: string | null;
}

// Task #49: event attachments.
export type AttachmentKind = 'evidence' | 'photo' | 'document' | 'signed_form' | 'other';

export interface EventAttachment {
  attachment_id: number;
  calendar_entry_id: number;
  day_index: number | null;
  kind: AttachmentKind;
  file_url: string;
  filename: string;
  content_type: string | null;
  file_size_bytes: number | null;
  description: string | null;
  uploaded_at: string;
  uploaded_by: number;
}

export interface EventAttachmentCreateInput {
  day_index?: number | null;
  kind?: AttachmentKind;
  file_url: string;
  filename: string;
  content_type?: string | null;
  file_size_bytes?: number | null;
  description?: string | null;
}

// Task #52: public sign-in flow.
export interface SignInCandidate {
  attendee_id: number;
  guest_id: number | null;
  full_name: string;
  already_present: boolean;
}

export interface SignInLookupResult {
  candidates: SignInCandidate[];
  event_title: string;
}

export interface SignInSubmitInput {
  attendee_id: number;
  day_index: number;
  guest_id?: number | null;
  signature_data?: string | null;
  signature_typed?: string | null;
  check_in_method?: 'signature_drawn' | 'signature_typed' | 'qr_self' | 'walk_in';
}

// Phase 2: Calendar event CRUD types
export interface EventCreateInput {
  entry_title: string;
  entry_description?: string | null;
  start_date: string;
  end_date: string;
  audience?: Audience;
  location_details?: string | null;
  venue_details?: string | null;
  event_type?: string;
  max_attendees?: number | null;
  registration_deadline?: string | null;
  materials?: string | null;
  source_reference_key?: string | null;
  resource_id?: number | null;
  // Task #3: bookable staff resources — array of shared_resource.resource_id
  // values where resource_type='staff'. The backend creates one
  // resource_booking per (staff, day) and uses the GIST exclusion constraint
  // to catch conflicts the same way it does for rooms.
  staff_resource_ids?: number[];
  // Task #15: online + hybrid event support.
  format?: EventFormat;
  online_provider?: OnlineProvider | null;
  online_meeting_url?: string | null;
  online_meeting_id?: string | null;
  online_meeting_passcode?: string | null;
  online_join_instructions?: string | null;
  online_url_visibility?: OnlineUrlVisibility;
  online_max_attendees?: number | null;
  slug?: string | null;
  hero_photo_url?: string | null;
  video_embed_url?: string | null;
  cost_text?: string | null;
  stipend_text?: string | null;
  doors_open_text?: string | null;
  provisions?: Provision[];
  agenda_md?: string | null;
  about_md?: string | null;
  presenters_md?: string | null;
  // Phase 026
  series_id?: number | null;
  sequence_number?: number | null;
  instance_label?: string | null;
  prereq_event_ids?: number[];
  prereq_text?: string | null;
  // Phase 028
  days?: EventDay[];
  // Round 3
  is_recurring_template?: boolean;
  parent_template_id?: number | null;
  instance_notes?: string | null;
  // Round 4
  is_draft?: boolean;
  tag_ids?: number[];
}

export interface SharedResource {
  resource_id: number;
  resource_name: string;
  resource_type: string;       // 'space' | 'staff' | 'equipment'
  capacity: number | null;
  description: string | null;
  // Task #3: optional staff metadata. Populated for resource_type='staff'.
  staff_user_id?: number | null;
  hourly_rate?: number | null;
  color_hex?: string | null;
  display_order?: number;
}

// Task #13: CMS page types.
export type CmsPageStatus = 'draft' | 'published';
export type CmsPageLayout = 'standard' | 'landing' | 'simple';

export interface CmsPage {
  page_id: number;
  slug: string;
  title: string;
  status: CmsPageStatus;
  layout: CmsPageLayout;
  body_md: string;
  hero_image_url?: string | null;
  hero_eyebrow?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  og_image_url?: string | null;
  nav_order?: number | null;
  nav_label?: string | null;
  published_at?: string | null;
  created_at?: string;
  updated_at?: string;
  is_archived?: boolean;
}

// Lightweight shape returned by /api/pages (no body_md, no hero — just the
// fields the marketing site needs for nav + sitemap).
export interface CmsPageSummary {
  page_id: number;
  slug: string;
  title: string;
  nav_label?: string | null;
  nav_order?: number | null;
  layout: CmsPageLayout;
  seo_title?: string | null;
  seo_description?: string | null;
  published_at?: string | null;
  updated_at?: string;
}

export interface CmsPageInput {
  slug: string;
  title: string;
  status?: CmsPageStatus;
  layout?: CmsPageLayout;
  body_md?: string;
  hero_image_url?: string | null;
  hero_eyebrow?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  og_image_url?: string | null;
  nav_order?: number | null;
  nav_label?: string | null;
}

export type CmsPageUpdateInput = Partial<CmsPageInput>;

// Task #16: admin-side farmer types.
export type ProgramCode = 'amp' | 'lfpp';

export interface AdminFarmer {
  farmer_id: number;
  email: string;
  full_name: string;
  phone: string | null;
  email_verified: boolean;
  has_password: boolean;
  last_login_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  invited_at: string | null;
  invite_sent_at: string | null;
  invited_by_name: string | null;
  registration_count: number;
  /** S4 read-switch: enrollment-backed AMP status (current program year),
   *  null when the farmer's farms have no enrollment row. */
  amp_enrollment_status: string | null;
  /** S6 (047d DROP): LFPP membership is a grants.lfpp_participation existence
   *  flag per row — the retired program-code array is gone. */
  lfpp_participant: boolean;
}

// S6 (047d DROP): the request field is `programs` (the admin's program-code
// selection); there is no backing membership array column anymore.
export interface AdminFarmerCreateInput {
  email: string;
  full_name: string;
  phone?: string | null;
  programs?: ProgramCode[];
}

export interface AdminFarmerUpdateInput {
  full_name?: string;
  phone?: string | null;
  programs?: ProgramCode[];
  is_active?: boolean;
}

export interface AdminFarmerBulkRow {
  email: string;
  full_name: string;
  phone?: string | null;
  programs?: ProgramCode[];
}

export interface AdminFarmerBulkResult {
  created: number;
  skipped: number;
  invites_sent: number;
  invites_failed: number;
  details: {
    created: Array<{ email: string; farmer_id: number }>;
    skipped: Array<{ email: string; reason: string }>;
    invites_sent: string[];
    invites_failed: Array<{ email: string; reason: string }>;
  };
}

// ----------------------------------------------------------------------------
// Build S1 (Task #21): farm identity + members.
// ----------------------------------------------------------------------------
export type RelationshipToFarm =
  | 'owner' | 'operator' | 'manager' | 'spouse' | 'family' | 'agent';

// List-row shape from GET /api/admin/farms (includes rollup counts).
export interface AdminFarm {
  farm_id: number;
  farm_code: string;
  farm_name: string;
  legal_entity_name: string | null;
  county: string | null;
  state_code: string;
  usda_farm_number: string | null;
  service_center: string | null;
  amp_participant: boolean;
  w9_on_file: boolean;
  is_test: boolean;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  member_count: number;
  subfield_count: number;
  unresolved_subfield_count: number;
}

export interface FarmKpis {
  total_farms: number;
  amp_participants: number;
  farms_with_unresolved_fsa: number;
  members_on_email_chain: number;
}

export interface FarmMember {
  member_id: number;
  farm_id: number;
  farmer_id: number | null;
  member_name: string;
  email: string | null;
  phone: string | null;
  relationship_to_farm: RelationshipToFarm;
  is_primary_contact: boolean;
  on_email_chain: boolean;
  signature_authority: boolean;
  title_role: string | null;
  valid_from: string;
  valid_to: string | null;
  created_at: string;
  updated_at: string;
}

export interface FarmFsaField {
  farm_fsa_field_id: number;
  farm_fsa_tract_id: number;
  fsa_farm_number: string;
  fsa_tract_number: string;
  fsa_field_number: string;
  clu_identifier: string | null;
  field_name: string | null;
  reported_acres: string | null;
  land_use_code: string | null;
  irrigated_flag: boolean | null;
}

export interface FarmFsaTract {
  farm_fsa_tract_id: number;
  farm_fsa_profile_id: number;
  fsa_state_code: string;
  fsa_county_code: string;
  fsa_farm_number: string;
  fsa_tract_number: string;
  tract_name: string | null;
  total_acres: string | null;
  json_config: Record<string, unknown> | null;
  fields: FarmFsaField[];
}

export interface FarmFsaProfile {
  farm_fsa_profile_id: number;
  program_year: number;
  producer_name: string;
  authorization_ref: string | null;
  authorization_date: string | null;
  programs_enrolled: unknown;
  json_config: Record<string, unknown> | null;
  tracts: FarmFsaTract[];
}

export interface Subfield {
  subfield_id: number;
  subfield_code: string;
  farm_fsa_field_id: number | null;
  prior_uif_subfield_id: string | null;
  planned_acres: string | null;
  vegetation_cover: string | null;
  irrigation_type: string | null;
  tillage_system: string | null;
  map_reference: string | null;
  notes: string | null;
  link_note: string | null;
  fsa_tract_number: string | null;
  fsa_field_number: string | null;
}

export interface FarmEmailRecipient {
  member_id: number;
  member_name: string;
  email: string;
  is_primary_contact: boolean;
}

// Detail shape from GET /api/admin/farms/:id (farm row without rollup counts).
export interface AdminFarmDetail {
  farm: {
    farm_id: number;
    farm_code: string;
    farm_name: string;
    legal_entity_name: string | null;
    county: string | null;
    state_code: string;
    address_line_1: string | null;
    address_line_2: string | null;
    address_city: string | null;
    address_state: string | null;
    address_zip: string | null;
    usda_farm_number: string | null;
    service_center: string | null;
    conservation_district: string | null;
    amp_participant: boolean;
    w9_on_file: boolean;
    f2c_farm_id: number | null;
    is_archived: boolean;
    json_config: Record<string, unknown> | null;
    created_at: string;
    updated_at: string;
  };
  members: FarmMember[];
  fsa: FarmFsaProfile[];
  subfields: Subfield[];
  email_recipients: FarmEmailRecipient[];
}

export interface FarmCreateInput {
  farm_code: string;
  farm_name: string;
  legal_entity_name?: string | null;
  county?: string | null;
  state_code?: string;
  address_line_1?: string | null;
  address_line_2?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_zip?: string | null;
  usda_farm_number?: string | null;
  service_center?: string | null;
  conservation_district?: string | null;
  amp_participant?: boolean;
  // w9_on_file is deliberately absent from this WRITE payload: the W-9 badge is
  // derived, not asserted, so there is nothing for a client to set (defect doc
  // §V.2 Ruling 2). It remains present on the farm READ types, where the server
  // returns it as a computed value.
}
export type FarmUpdateInput = Partial<FarmCreateInput>;

export interface FarmMemberCreateInput {
  member_name: string;
  email?: string | null;
  phone?: string | null;
  relationship_to_farm: RelationshipToFarm;
  is_primary_contact?: boolean;
  on_email_chain?: boolean;
  signature_authority?: boolean;
  title_role?: string | null;
  farmer_id?: number | null;
}
export type FarmMemberUpdateInput = Partial<FarmMemberCreateInput>;

// Farmer-facing shape from GET /api/farmer/farms.
export interface MyFarm {
  farm_id: number;
  farm_code: string;
  farm_name: string;
  county: string | null;
  state_code: string;
  amp_participant: boolean;
  relationship_to_farm: RelationshipToFarm;
  is_primary_contact: boolean;
  on_email_chain: boolean;
  signature_authority: boolean;
  title_role: string | null;
}

// ----------------------------------------------------------------------------
// Build S2 (Task #18): documents.
// ----------------------------------------------------------------------------
export type DocumentStatus = 'draft' | 'in_review' | 'approved' | 'final' | 'superseded' | 'void';
export type DocumentScopeType = 'farm' | 'farmer' | 'none';
// Full scope vocabulary as stored (DB CHECK on documents.document.scope_type).
// Creation in this build is limited to DocumentScopeType; later builds write
// the rest, and admin READ shapes must still render them.
export type DocumentScopeTypeAny =
  | DocumentScopeType
  | 'enrollment' | 'agreement' | 'service_order' | 'batch' | 'program';
export type DocumentSensitivity = 'public' | 'internal' | 'confidential' | 'restricted';

export interface DocumentTypeInfo {
  type_code: string;
  label: string;
  lane: 'P' | 'G' | 'C' | 'N';
  default_sensitivity: DocumentSensitivity;
  sort_order: number;
}

export interface AdminDocument {
  document_id: string;
  type_code: string;
  type_label: string;
  lane: 'P' | 'G' | 'C' | 'N';
  title: string;
  scope_type: DocumentScopeTypeAny;
  scope_id: string | null;
  farm_id: number | null;
  farm_code: string | null;
  status: DocumentStatus;
  visibility: 'uif_admin' | 'producer_shared' | 'public';
  sensitivity_level: DocumentSensitivity;
  source: string;
  current_version_id: string | null;
  created_at: string;
  updated_at: string;
  file_name: string | null;
  byte_size: string | null;
  mime_type: string | null;
  current_version_no: number | null;
  version_count: number;
  share_count: number;
}

// ----- Build S3: signatures + agreements -----
export type EnvelopeStatus =
  | 'draft' | 'sent' | 'partially_signed' | 'completed' | 'declined' | 'voided' | 'expired';
export type PartyStatus = 'pending' | 'notified' | 'viewed' | 'signed' | 'declined';

export interface AdminEnvelope {
  envelope_id: string;
  subject_type: string;
  subject_id: string;
  status: EnvelopeStatus;
  signing_order: 'sequential' | 'parallel';
  title: string;
  sent_at: string | null;
  completed_at: string | null;
  expires_at: string | null;
  created_at: string;
  subject_title: string | null;
  farm_id: number | null;
  farm_code: string | null;
  party_count: number;
  signed_count: number;
}

export interface EnvelopeParty {
  party_id: string;
  order_index: number;
  signer_kind: 'UIF' | 'PRODUCER' | 'CLIENT' | 'WITNESS' | 'EXTERNAL';
  farm_member_id: number | null;
  admin_user_id: number | null;
  display_name: string;
  email: string;
  title_role: string | null;
  status: PartyStatus;
  notified_at: string | null;
  first_viewed_at: string | null;
  signed_at: string | null;
  declined_at: string | null;
  declined_reason: string | null;
  signature_method: string | null;
  consent_esign_at: string | null;
  has_pending_token: boolean;
}

export interface AdminEnvelopeDetail {
  envelope: AdminEnvelope & { signing_instructions: string | null; audit_trail: unknown[] };
  parties: EnvelopeParty[];
  subject: { document_id: string; title: string; status: string; farm_code: string | null; file_name: string | null } | null;
  agreement: { agreement_id: string; agreement_type: string; status: string; template_version: string } | null;
  /** F-4: present when the CALLER is a named UIF party — the admin ceremony context. */
  my_party: { party_id: string; order_index: number; status: PartyStatus; display_name: string; consent_recorded: boolean; signed_at: string | null; my_turn: boolean } | null;
  consent_language: string | null;
  consent_language_version: string;
}

export interface EnvelopeCreateInput {
  subject_type: 'document';
  subject_id: string;
  title: string;
  signing_order?: 'sequential' | 'parallel';
  signing_instructions?: string | null;
  expires_in_days?: number;
  agreement_id?: string;
}

export interface EnvelopePartyInput {
  signer_kind: 'UIF' | 'PRODUCER' | 'EXTERNAL';
  order_index: number;
  farm_member_id?: number;
  admin_user_id?: number;
  external_email?: string;
  external_name?: string;
  title_role?: string | null;
}

export interface AdminAgreement {
  agreement_id: string;
  agreement_type: string;
  farm_id: number | null;
  farm_code: string | null;
  farm_name: string | null;
  template_version: string;
  status: 'draft' | 'out_for_signature' | 'executed' | 'amended' | 'terminated' | 'expired';
  effective_date: string | null;
  term_end: string | null;
  envelope_id: string | null;
  executed_document_id: string | null;
  created_at: string;
  amendment_count: number;
}

export interface AdminAgreementDetail {
  agreement: AdminAgreement & { governing_notes: string | null; envelope_status: string | null };
  exhibits: Array<{ exhibit_id: string; exhibit_code: string; exhibit_version: number; published_at: string; effective_from: string; document_id: string; is_current: boolean }>;
  amendments: Array<{ amendment_id: string; amendment_no: number; summary: string; envelope_id: string; executed_document_id: string; effective_date: string }>;
}

export interface AgreementCreateInput {
  agreement_type: 'AMP_PARTICIPATION';
  farm_id: number;
  template_version: string;
  term_end?: string | null;
  governing_notes?: string | null;
  // Link a document via adminEnvelopeCreate({ agreement_id }) — the create
  // payload carries no document_id (Copilot r4: no ignored fields).
}
export interface AgreementPatchInput {
  template_version?: string;
  term_end?: string | null;
  governing_notes?: string | null;
  status?: 'terminated' | 'expired';
}
export interface AgreementAmendInput {
  document_id: string;
  summary: string;
  title: string;
  effective_date?: string;
}
export interface ExhibitPublishInput {
  exhibit_code: string;
  document_id: string;
  effective_from: string;
}

export interface FarmerEnvelope {
  envelope_id: string;
  title: string;
  envelope_status: EnvelopeStatus;
  signing_order: 'sequential' | 'parallel';
  expires_at: string | null;
  sent_at: string | null;
  subject_title: string | null;
  farm_code: string;
  farm_name: string;
  party_id: string;
  order_index: number;
  my_status: PartyStatus;
  signed_at: string | null;
  consent_recorded: boolean;
}

export interface FarmerEnvelopeDetail {
  envelope: { envelope_id: string; title: string; status: EnvelopeStatus; signing_order: string; signing_instructions: string | null; expires_at: string | null };
  my_party: { party_id: string; order_index: number; status: PartyStatus; display_name: string; consent_recorded: boolean; signed_at: string | null; my_turn: boolean };
  co_signers: Array<{ order_index: number; display_name: string; status: PartyStatus }>;
  subject_title: string | null;
  preview_url: string | null;
  consent_language: string;
  consent_language_version: string;
}

export interface ExternalSignDetail {
  envelope: { title: string; signing_instructions: string | null; expires_at: string | null };
  my_party: { display_name: string; title_role: string | null; status: PartyStatus; consent_recorded: boolean };
  subject_title: string | null;
  preview_url: string | null;
  consent_language: string;
  consent_language_version: string;
}

export interface SignInput {
  method: 'typed' | 'drawn';
  typed_name?: string;
  drawn_png_base64?: string;
}

export interface DocumentKpis {
  total_documents: number;
  pending_approval: number;
  shared_documents: number;
  restricted_documents: number;
}

export interface DocumentVersion {
  version_id: string;
  version_no: number;
  file_name: string;
  mime_type: string;
  byte_size: string;
  sha256: string;
  uploaded_at: string;
  notes: string | null;
  uploaded_by_name: string | null;
}

export interface DocumentApproval {
  approval_id: string;
  version_id: string;
  version_no: number;
  role_label: 'UIF_REVIEWER' | 'PRODUCER' | 'EXTERNAL';
  status: 'requested' | 'approved' | 'changes_requested' | 'expired';
  requested_at: string;
  acted_at: string | null;
  comment: string | null;
  requested_of_admin_id: number | null;
  admin_name: string | null;
  requested_of_member_id: number | null;
  member_name: string | null;
}

export interface DocumentShare {
  share_id: string;
  farm_id: number;
  farm_code: string;
  farm_name: string;
  shared_at: string;
  acknowledged_at: string | null;
  first_downloaded_at: string | null;
  shared_by_name: string | null;
}

export interface AdminDocumentDetail {
  document: AdminDocument & { retention_note: string | null; farm_name: string | null; is_archived: boolean };
  versions: DocumentVersion[];
  approvals: DocumentApproval[];
  shares: DocumentShare[];
}

export interface DocumentUploadUrlInput {
  title: string;
  type_code: string;
  // S4 widened creatable scopes to include 'enrollment' (backend
  // documentScopeCreatableSchema); S6b uploads its PDFs enrollment-scoped.
  scope_type: DocumentScopeType | 'enrollment';
  scope_id?: string | null;
  farm_id?: number | null;
  file_name: string;
  mime_type: string;
  byte_size: number;
  sha256_base64: string;
}

export interface DocumentUploadUrlResult {
  document_id: string;
  version_id: string;
  version_no?: number;
  upload_url: string;
  expires_in: number;
}

export interface FarmerDocument {
  document_id: string;
  title: string;
  type_code: string;
  type_label: string;
  farm_id: number;
  farm_code: string;
  farm_name: string;
  shared_at: string;
  acknowledged_at: string | null;
  file_name: string;
  mime_type: string;
  byte_size: string;
  uploaded_at: string;
}

// Task #11: farmer-facing registration summary.
export interface FarmerRegistration {
  attendee_id: number;
  global_id: string;
  event_id: number;
  calendar_entry_id: number;
  registration_status: string;
  attendance_mode: 'in_person' | 'online';
  attendee_count: number | null;
  special_requests: string | null;
  confirmed_at: string | null;
  registered_at: string;
  title: string;
  slug: string | null;
  start_date: string;
  end_date: string;
  location: string | null;
  format: 'in_person' | 'online' | 'hybrid';
  online_provider: string | null;
  online_meeting_url: string | null;
  online_meeting_id: string | null;
  online_meeting_passcode: string | null;
  online_join_instructions: string | null;
  is_draft: boolean;
  is_past: boolean;
  can_join: boolean;
  can_cancel: boolean;
  attendance: Array<{
    attendee_id: number;
    day_index: number;
    present: boolean;
    check_in_time: string | null;
    check_in_method: string | null;
  }>;
}

// Task #10: farmer account types.
export interface Farmer {
  farmer_id: number;
  email: string;
  full_name: string;
  phone?: string | null;
  email_verified?: boolean;
  last_login_at?: string | null;
  created_at?: string;
  f2c_user_id?: string | null;
}

export interface FarmerSignupInput {
  email: string;
  password: string;
  full_name: string;
  phone?: string | null;
}

export interface FarmerLoginInput {
  email: string;
  password: string;
}

export interface FarmerLoginResult {
  token: string;
  expires_at: string;
  farmer: Pick<Farmer, 'farmer_id' | 'email' | 'full_name'>;
}

export interface FarmerProfileUpdateInput {
  full_name?: string;
  phone?: string | null;
}

// Task #9: email subscriber types.
export type SubscriberStatus = 'pending' | 'confirmed' | 'unsubscribed';

export interface Subscriber {
  subscriber_id: number;
  email: string;
  full_name: string | null;
  status: SubscriberStatus;
  source: string | null;
  tag_interests: string[];
  subscribed_at: string;
  confirmed_at: string | null;
  unsubscribed_at: string | null;
  last_send_at: string | null;
}

export interface SubscriberInput {
  email: string;
  full_name?: string | null;
  source?: string | null;
  tag_interests?: string[];
}

export interface SubscriberBlastInput {
  subject: string;
  body_md: string;
  recipient_filter?: 'confirmed' | 'confirmed_and_pending';
  subscriber_ids?: number[];
}

// Task #8: cross-event budget report payload.
export interface BudgetReport {
  range: {
    from: string;
    to: string;
    event_count: number;
    line_count: number;
    include_drafts: boolean;
  };
  totals: {
    expected_expense: string;
    actual_expense: string;
    expected_income: string;
    actual_income: string;
    net_expected: string;
    net_actual: string;
  };
  by_category: Array<{
    category: string;
    is_income: boolean;
    expected: string;
    actual: string;
    line_count: number;
  }>;
  by_funding: Array<{
    funding_source: string;
    expected: string;
    actual: string;
  }>;
  by_event: Array<{
    calendar_entry_id: number;
    title: string;
    start_date: string;
    audience: Audience;
    is_draft: boolean;
    expected_expense: string;
    actual_expense: string;
    expected_income: string;
    actual_income: string;
    net_expected: string;
    net_actual: string;
    line_count: number;
  }>;
}

// Task #5: admin-only tag row with usage count + archive metadata.
export interface AdminTag extends EventTag {
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  event_count: number;
}

export interface TagInput {
  tag_name: string;
  tag_category?: TagCategory;
  display_order?: number | null;
  description?: string | null;
  is_active?: boolean;
}

export type TagUpdateInput = Partial<TagInput>;

// Task #3: admin-only resource row with extra audit + linked admin_user
// display info. Used by ResourcesPanel.
export interface AdminResource extends SharedResource {
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  staff_display_name?: string | null;
  staff_username?: string | null;
  staff_email?: string | null;
}

export interface ResourceInput {
  resource_name: string;
  resource_type?: 'space' | 'staff' | 'equipment';
  capacity?: number | null;
  description?: string | null;
  staff_user_id?: number | null;
  hourly_rate?: number | null;
  color_hex?: string | null;
  display_order?: number | null;
  is_active?: boolean;
}

export type ResourceUpdateInput = Partial<ResourceInput>;

export type EventUpdateInput = Partial<EventCreateInput>;

export interface EventCreateResult {
  calendar_entry_id: number;
  event_id: number;
  booking_id: number | null;
}

// Phase 2: Resource booking types
export interface BookingListResponse {
  bookings: BookingRow[];
}

// Wide enough to cover both full UIF rows and AGC-redacted rows.
// Optional fields are absent in the redacted view.
export interface BookingRow {
  booking_id: number;
  resource_id: number;
  resource_name: string;
  start: string;
  end: string;
  booking_status: BookingStatus;
  booking_source: BookingSource | null;
  event_type?: string | null;
  entry_id?: number | null;
  notes?: string | null;
  event_title?: string | null;
  audience?: Audience | null;
}

export interface BookingCreateInput {
  resource_id: number;
  start: string;
  end: string;
  booking_status?: BookingStatus;
  notes?: string | null;
  entry_id?: number | null;
}

export type BookingUpdateInput = Partial<BookingCreateInput>;

// Phase 2: Budget types
export interface BudgetLineRow {
  line_id: number;
  calendar_entry_id: number;
  category: string;
  description: string | null;
  expected_amount: number;
  actual_amount: number;
  is_income: boolean;
  notes: string | null;
  lfpp_pct: number;
  amp_pct: number;
  uif_pct: number;
  total_pct: number;
}

export interface BudgetTotals {
  calendar_entry_id?: number;
  series_id?: number;
  series_label?: string;
  total_expected_expense: number;
  total_actual_expense: number;
  total_expected_income: number;
  total_actual_income: number;
  net_expected: number;
  net_actual: number;
  expense_line_count?: number;
  income_line_count?: number;
  entry_count?: number;
  total_line_count?: number;
}

export interface BudgetResponse {
  lines: BudgetLineRow[];
  event_totals: BudgetTotals | null;
  series_totals: BudgetTotals | null;
}

export interface FundingSplitEntry {
  funding_source: FundingSource;
  percentage: number;
}

export interface BudgetLineCreateInput {
  category: string;
  description?: string | null;
  expected_amount?: number;
  actual_amount?: number;
  is_income?: boolean;
  notes?: string | null;
  funding_split?: FundingSplitEntry[];
}

export type BudgetLineUpdateInput = Partial<BudgetLineCreateInput>;

// ----- Build S4: programs + enrollment + eligibility (Task #25) -----

export interface AdminProgramYear {
  program_year_id: string;
  program_id: string;
  year_no: number;
  enrollment_open_date: string | null;
  enrollment_close_date: string | null;
  template_agreement_version: string | null;
  annual_budget: string | null;
  notes: string | null;
  is_current: boolean;
  enrollment_policy: { mode?: string; typical_waves?: string[]; horizon_year?: number; target_2028?: string } | null;
  eligibility_requirements: Array<{ requirement_type: string; applies?: boolean; note?: string }> | null;
  enrollment_total: number;
  enrollment_enrolled: number;
}

export interface AdminProgram {
  program_id: string;
  program_code: string;
  program_number: string | null;
  program_name: string;
  program_category: string | null;
  funding_agency: string | null;
  award_date: string | null;
  period_start: string | null;
  period_end: string | null;
  total_award: string | null;
  federal_share: string | null;
  match_required: string | null;
  status: string;
  governing_regulation_ref: string | null;
  years: AdminProgramYear[];
}

export type EnrollmentStatus =
  | 'invited' | 'application_started' | 'application_submitted' | 'under_review'
  | 'conditionally_approved' | 'enrolled' | 'withdrawn' | 'terminated' | 'completed';

export interface EnrollmentSummary {
  enrollment_id: string;
  farm_id: number;
  program_year_id: string;
  agreement_id: string | null;
  status: EnrollmentStatus;
  enrolled_at: string | null;
  withdrew_at: string | null;
  completed_at: string | null;
  cohort_tag: string | null;
  program_participant_id: string | null;
  created_at: string | null;
  farm_code: string;
  farm_name: string;
  year_no: number;
  program_code: string;
  snapshot_complete: boolean | null;
}

export interface EligibilityRequirement {
  requirement_type: string;
  applies: boolean;
  is_satisfied: boolean;
  satisfying_document_id: string | null;
  satisfying_agreement_id: string | null;
  satisfied_at: string | null;
  note: string | null;
}

export interface EligibilitySnapshot {
  snapshot_id: string;
  computed_at: string | null;
  requirements: EligibilityRequirement[];
  is_complete: boolean;
}

export interface EnrollmentHistoryEntry {
  history_id: string;
  from_status: string | null;
  to_status: string;
  transitioned_at: string | null;
  transitioned_by: number | null;
  reason: string | null;
}

export interface AdminEnrollmentDetail {
  enrollment: EnrollmentSummary & { program_name: string };
  snapshot: EligibilitySnapshot | null;
  agreement: { agreement_id: string; status: string; template_version: string; effective_date: string | null } | null;
  history: EnrollmentHistoryEntry[];
  allowed_transitions: EnrollmentStatus[];
}

export interface FarmerEnrollment extends EnrollmentSummary {
  program_name: string;
}

// ----------------------------------------------------------------------------
// Build S6b: record externally-executed AMP agreements (Task #37)
// ----------------------------------------------------------------------------
export type ExternalExecutionChannel = 'external_docusign' | 'external_paper' | 'external_email';

export interface RecordExternalAgreementPayload {
  execution_date: string;                 // YYYY-MM-DD, <= today (server-enforced)
  execution_channel?: ExternalExecutionChannel;
  docusign_envelope_uuid?: string | null;
  signer_names: string[];
  template_version?: string | null;       // defaults to the program year's template
  agreement_document_id: string;          // finalized AMP_AGREEMENT doc
  certificate_document_id?: string | null; // finalized EXECUTION_CERTIFICATE (required for external_docusign)
  voc_document_id?: string | null;        // finalized VOC_ASSESSMENT (required if none on file for the farm)
  include_test?: boolean;
  // Wave 2 item 3: operator-waivable VOC. Waiving skips the VOC requirement and
  // records who/when/why on the agreement; the reason is mandatory (422 without
  // it). VOC stays visibly outstanding for the farm — it is not marked satisfied.
  voc_waived?: boolean;
  voc_waiver_reason?: string | null;
}

export interface RecordExternalBatchRow extends RecordExternalAgreementPayload {
  enrollment_id: string;
}

export interface RecordExternalBatchResult {
  enrollment_id: string;
  success: boolean;
  code: string | null;
  error: string | null;
  enrollment?: EnrollmentSummary;
}

// Task #44 Phase B — public stipend-address collection.
export interface AttendeeAddress {
  line1: string;
  line2?: string | null;
  city: string;
  state: string;
  zip: string;
}

export interface AttendeeInfoLookup {
  event: { title: string; date: string | null };
  attendee: { full_name: string | null; has_submitted_address: boolean };
}

export interface VocOnFileResult {
  farm_id: number;
  on_file: boolean;
  documents: Array<{ document_id: string; title: string; created_at: string | null }>;
}

// ----------------------------------------------------------------------------
// Build S5: tracks + milestones (Task #22)
// ----------------------------------------------------------------------------
export type TrackCode = 'CONS' | 'PROD' | 'FSAF' | 'MKTG';
export type StageCode = 'DEVELOP' | 'IMPLEMENT' | 'COMPLETE' | 'CONTINUOUS_IMPROVEMENT';
export type TrackPlanStatus = 'available' | 'active' | 'on_hold' | 'withdrawn' | 'completed';
export type MilestoneStatus =
  | 'not_started' | 'in_progress' | 'submitted' | 'under_review'
  | 'completed' | 'not_applicable' | 'waived';

export interface TrackPlan {
  track_plan_id: string;
  enrollment_id: string;
  track_code: TrackCode;
  status: TrackPlanStatus;
  plan_summary: string | null;
  plan_document_id: string | null;
  activated_at: string | null;
  completed_at: string | null;
  farm_code: string;
  farm_name: string;
}

export interface TrackPlanSummary extends TrackPlan {
  milestone_total: number;
  milestone_completed: number;
  needs_plan_document?: boolean;
}

export interface MilestoneEvidence {
  evidence_id: string;
  farm_milestone_id: string;
  document_id: string | null;
  agreement_id: string | null;
  envelope_id: string | null;
  evidence_type: string;
  attached_at: string | null;
  attached_by: number;
  notes: string | null;
}

// Core milestone shape — exactly what toFarmMilestoneResponse returns (e.g. the
// milestone-transition response). Detail reads return FarmMilestone (below).
export interface FarmMilestoneCore {
  farm_milestone_id: string;
  enrollment_id: string;
  track_plan_id: string;
  milestone_def_id: string;
  status: MilestoneStatus;
  started_at: string | null;
  submitted_at: string | null;
  completed_at: string | null;
  evidence_summary: string | null;
  not_applicable_reason: string | null;
  waived_by: number | null;
  waived_reason: string | null;
}

// Detail milestone shape — the track-detail reads (farmer + admin) via
// toMilestoneDetailResponse: core + stage/label/evidence + computed missing_evidence.
export interface FarmMilestone extends FarmMilestoneCore {
  stage_code: StageCode;
  label: string;
  def_is_draft?: boolean;
  evidence_required: string[] | null;
  missing_evidence: string[];   // S5p (Task #34): evidence_required codes not yet satisfied
  allowed_transitions?: MilestoneStatus[];
  evidence: MilestoneEvidence[];
}

export interface AdminTrackDetail {
  track_plan: TrackPlan & { year_no: number };
  allowed_transitions: TrackPlanStatus[];
  milestones: FarmMilestone[];
}

export interface FarmerTrackDetail {
  track_plan: TrackPlan;
  milestones: FarmMilestone[];
  practices: PracticeInstance[];   // S6: surfaced under CONS-IMPLEMENT
}

export interface MilestoneDef {
  milestone_def_id: string;
  track_code: TrackCode;
  stage_code: StageCode;
  def_version: number;
  program_year_id: string | null;
  label: string;
  description: string | null;
  exit_criteria: string | null;
  evidence_required: string[] | null;
  requires_cpa52: boolean;
  is_draft: boolean;
  sort_order: number;
}

// ----------------------------------------------------------------------------
// Build S6 — practices + CPA-52 (Task #26)
// ----------------------------------------------------------------------------
export type UnitOfMeasure = 'acres' | 'sqft' | 'feet' | 'count';
export type PracticeStatus = 'planned' | 'in_progress' | 'implemented' | 'verified' | 'terminated' | 'historic';
export type Cpa52Status = 'draft' | 'submitted' | 'signed' | 'active' | 'superseded' | 'void';

export interface PracticeDef {
  practice_def_id: string;
  nrcs_code: string;
  practice_name: string;
  category: string | null;
  unit_of_measure: UnitOfMeasure | null;
  default_lifespan_years: number | null;
  description: string | null;
  is_active: boolean;
  sort_order: number;
}

export interface PracticeInstance {
  practice_instance_id: string;
  subfield_id: number;
  farm_id: number;
  enrollment_id: string | null;
  program_year_id: string | null;
  practice_def_id: string;
  track_code: TrackCode | null;
  status: PracticeStatus;
  planned_amount: number | null;
  implemented_amount: number | null;
  planned_at: string | null;
  implemented_at: string | null;
  verified_at: string | null;
  verified_by: number | null;
  notes: string | null;
  // present on joined reads (admin list/detail, farmer, track detail):
  nrcs_code?: string;
  practice_name?: string;
  unit_of_measure?: UnitOfMeasure | null;
}

export interface Cpa52Submission {
  cpa52_submission_id: string;
  farm_id: number;
  enrollment_id: string;
  envelope_id: string | null;
  executed_document_id: string | null;
  status: Cpa52Status;
  submission_number: string | null;
  effective_date: string | null;
  submitted_at: string | null;
  signed_at: string | null;
  notes: string | null;
  practice_count?: number;
}

export interface Cpa52SubmissionPractice {
  submission_practice_id: string;
  cpa52_submission_id: string;
  practice_instance_id: string;
  role: string;
  notes: string | null;
  nrcs_code?: string;
  practice_name?: string;
}

export interface DocumentTypeVocab {
  type_code: string;
  label: string;
}

// ----------------------------------------------------------------------------
// API surface — flat methods to keep call sites simple.
// ----------------------------------------------------------------------------

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}` };
}

export const api = {
  // ----- Public -----
  events: (opts?: { includePast?: boolean; includeTemplates?: boolean; includeDrafts?: boolean; token?: string }) => {
    const params = new URLSearchParams();
    if (opts?.includePast)      params.set('include_past', 'true');
    if (opts?.includeTemplates) params.set('include_templates', 'true');
    if (opts?.includeDrafts)    params.set('include_drafts', 'true');
    const qs = params.toString() ? `?${params}` : '';
    const headers = opts?.token ? authHeaders(opts.token) : {};
    return request<{ events: PublicEvent[] }>(`/api/events${qs}`, { headers });
  },

  tags: () => request<{ tags: EventTag[] }>('/api/tags'),

  presenters: () => request<{ presenters: Presenter[] }>('/api/presenters'),

  createPresenter: (token: string, input: PresenterInput) =>
    request<{ presenter_id: number }>('/api/admin/presenters', {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  updatePresenter: (token: string, presenterId: number, patch: PresenterUpdateInput) =>
    request<{ presenter_id: number; updated: true }>(`/api/admin/presenters/${presenterId}`, {
      method: 'PATCH',
      headers: authHeaders(token),
      body: JSON.stringify(patch),
    }),

  archivePresenter: (token: string, presenterId: number) =>
    request<{ presenter_id: number; archived: true }>(`/api/admin/presenters/${presenterId}`, {
      method: 'DELETE',
      headers: authHeaders(token),
    }),

  // ----- Locations (Phase 3d-1i) -----
  locations: () => request<{ locations: Location[] }>('/api/locations'),

  createLocation: (token: string, input: LocationInput) =>
    request<{ location_id: number }>('/api/admin/locations', {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  updateLocation: (token: string, locationId: number, patch: LocationUpdateInput) =>
    request<{ location_id: number; updated: true }>(`/api/admin/locations/${locationId}`, {
      method: 'PATCH',
      headers: authHeaders(token),
      body: JSON.stringify(patch),
    }),

  archiveLocation: (token: string, locationId: number) =>
    request<{ location_id: number; archived: true }>(`/api/admin/locations/${locationId}`, {
      method: 'DELETE',
      headers: authHeaders(token),
    }),

  // ----- Series (Phase 026) -----
  series: () => request<{ series: Series[] }>('/api/series'),

  createSeries: (token: string, input: SeriesInput) =>
    request<{ series_id: number }>('/api/admin/series', {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  updateSeries: (token: string, seriesId: number, patch: SeriesUpdateInput) =>
    request<{ series_id: number; updated: true }>(`/api/admin/series/${seriesId}`, {
      method: 'PATCH',
      headers: authHeaders(token),
      body: JSON.stringify(patch),
    }),

  archiveSeries: (token: string, seriesId: number) =>
    request<{ series_id: number; archived: true }>(`/api/admin/series/${seriesId}`, {
      method: 'DELETE',
      headers: authHeaders(token),
    }),

  // Round 3: materialize N instances from a template
  expandSeries: (token: string, seriesId: number, input: { template_entry_id: number; start_date: string; max_instances?: number }) =>
    request<{ series_id: number; created_entry_ids: number[]; count: number }>(`/api/admin/series/${seriesId}/expand`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  // ----- UIF only: pre-signed photo upload (Phase 3d-1f) -----
  signUpload: (
    token: string,
    input: { filename: string; content_type: string; destination: 'presenters' | 'events' | 'locations' | 'evidence' },
  ) =>
    request<{ upload_url: string; public_url: string; key: string; expires_in: number }>(
      '/api/admin/uploads/sign',
      {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(input),
      },
    ),

  sharedResources: (token: string, type?: 'space' | 'staff' | 'equipment') =>
    request<{ resources: SharedResource[] }>(
      `/api/calendar/resources${type ? `?type=${type}` : ''}`,
      { headers: authHeaders(token) },
    ),

  // ----- Public: CMS pages (Task #13) -----
  publicPages: () =>
    request<{ pages: CmsPageSummary[] }>('/api/pages'),

  publicPage: (slug: string) =>
    request<{ page: CmsPage }>(`/api/pages/${encodeURIComponent(slug)}`),

  // ----- UIF only: CMS admin (Task #13) -----
  adminPages: (token: string) =>
    request<{ pages: CmsPage[] }>('/api/admin/pages', {
      headers: authHeaders(token),
    }),

  adminPage: (token: string, pageId: number) =>
    request<{ page: CmsPage }>(`/api/admin/pages/${pageId}`, {
      headers: authHeaders(token),
    }),

  createPage: (token: string, input: CmsPageInput) =>
    request<{ page_id: number }>('/api/admin/pages', {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  updatePage: (token: string, pageId: number, patch: CmsPageUpdateInput) =>
    request<{ page_id: number; updated: boolean }>(`/api/admin/pages/${pageId}`, {
      method: 'PATCH',
      headers: authHeaders(token),
      body: JSON.stringify(patch),
    }),

  publishPage: (token: string, pageId: number) =>
    request<{ page_id: number; published: true }>(`/api/admin/pages/${pageId}/publish`, {
      method: 'POST',
      headers: authHeaders(token),
    }),

  archivePage: (token: string, pageId: number) =>
    request<{ page_id: number; archived: true }>(`/api/admin/pages/${pageId}`, {
      method: 'DELETE',
      headers: authHeaders(token),
    }),

  // ----- Public: farmer account auth (Task #10) -----
  farmerSignup: (input: FarmerSignupInput) =>
    request<{ ok: boolean; farmer_id?: number; message: string }>('/api/farmer/signup', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  farmerVerifyEmail: (token: string) =>
    request<{ verified: boolean; email?: string; linked_registrations?: number; message?: string }>(
      `/api/farmer/verify/${encodeURIComponent(token)}`,
    ),

  farmerLogin: (input: FarmerLoginInput) =>
    request<FarmerLoginResult>('/api/farmer/login', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  farmerRequestPasswordReset: (email: string) =>
    request<{ ok: boolean; message: string }>('/api/farmer/password-reset/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  farmerConfirmPasswordReset: (token: string, password: string) =>
    request<{ ok: boolean; email?: string }>('/api/farmer/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  farmerMe: (token: string) =>
    request<{ farmer: Farmer }>('/api/farmer/me', {
      headers: authHeaders(token),
    }),

  farmerUpdateMe: (token: string, patch: FarmerProfileUpdateInput) =>
    request<{ farmer_id: number; updated: boolean }>('/api/farmer/me', {
      method: 'PATCH',
      headers: authHeaders(token),
      body: JSON.stringify(patch),
    }),

  // ----- Farmer-facing My Registrations (Task #11) -----
  farmerRegistrations: (token: string) =>
    request<{
      registrations: FarmerRegistration[];
      counts: { total: number; upcoming: number; past: number };
    }>('/api/farmer/registrations', {
      headers: authHeaders(token),
    }),

  farmerCancelRegistration: (token: string, attendeeId: number) =>
    request<{ attendee_id: number; registration_status: 'cancelled' }>(
      `/api/farmer/registrations/${attendeeId}/cancel`,
      { method: 'PATCH', headers: authHeaders(token) },
    ),

  // ----- Public: admin-invited farmer onboarding (Task #16) -----
  farmerOnboard: (token: string, password: string) =>
    request<{
      token: string; expires_at: string;
      farmer: { farmer_id: number; email: string; full_name: string };
      linked_registrations: number;
    }>('/api/farmer/onboard', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  // ----- UIF only: admin farmer CRUD + invite (Task #16) -----
  adminFarmers: (
    token: string,
    params?: { q?: string; program?: string; status?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.q)       qs.set('q', params.q);
    if (params?.program) qs.set('program', params.program);
    if (params?.status)  qs.set('status', params.status);
    const query = qs.toString();
    return request<{
      farmers: AdminFarmer[];
      program_counts: Array<{ program: string; count: number }>;
      lfpp_participation_count: number;
    }>(`/api/admin/farmers${query ? `?${query}` : ''}`, {
      headers: authHeaders(token),
    });
  },

  createFarmer: (token: string, input: AdminFarmerCreateInput) =>
    request<{ farmer_id: number }>('/api/admin/farmers', {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  updateFarmer: (token: string, farmerId: number, patch: AdminFarmerUpdateInput) =>
    request<{ farmer_id: number; updated: boolean }>(`/api/admin/farmers/${farmerId}`, {
      method: 'PATCH',
      headers: authHeaders(token),
      body: JSON.stringify(patch),
    }),

  archiveFarmer: (token: string, farmerId: number) =>
    request<{ farmer_id: number; archived: true }>(`/api/admin/farmers/${farmerId}`, {
      method: 'DELETE',
      headers: authHeaders(token),
    }),

  bulkFarmers: (
    token: string,
    farmers: AdminFarmerBulkRow[],
    sendInvites: boolean,
  ) =>
    request<AdminFarmerBulkResult>('/api/admin/farmers/bulk', {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ farmers, send_invites: sendInvites }),
    }),

  inviteFarmer: (token: string, farmerId: number) =>
    request<{ farmer_id: number; invite_sent: true }>(`/api/admin/farmers/${farmerId}/invite`, {
      method: 'POST',
      headers: authHeaders(token),
    }),

  // Task #17: program-scoped blast.
  farmerBlast: (
    token: string,
    input: {
      subject: string;
      body_md: string;
      programs?: ProgramCode[];
      only_onboarded?: boolean;
      farmer_ids?: number[];
      include_unsub?: boolean;
    },
  ) =>
    request<{
      sent: number; failed: number; total: number;
      failures?: Array<{ email: string; reason: string }>;
    }>('/api/admin/farmers/email', {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  // ----- Admin: farms + members (Build S1, Task #21) -----
  adminFarms: (
    token: string,
    params?: { q?: string; amp?: 'true' | 'false'; include_archived?: boolean; include_test?: boolean },
  ) => {
    const qs = new URLSearchParams();
    if (params?.q)                qs.set('q', params.q);
    if (params?.amp)              qs.set('amp', params.amp);
    if (params?.include_archived) qs.set('include_archived', 'true');
    if (params?.include_test)     qs.set('include_test', 'true');
    const query = qs.toString();
    return request<{ farms: AdminFarm[]; kpis: FarmKpis }>(
      `/api/admin/farms${query ? `?${query}` : ''}`,
      { headers: authHeaders(token) },
    );
  },

  adminFarm: (token: string, farmId: number) =>
    request<AdminFarmDetail>(`/api/admin/farms/${farmId}`, {
      headers: authHeaders(token),
    }),

  createFarm: (token: string, input: FarmCreateInput) =>
    request<{ farm_id: number }>('/api/admin/farms', {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  updateFarm: (token: string, farmId: number, patch: FarmUpdateInput) =>
    request<{ farm_id: number; updated: true }>(`/api/admin/farms/${farmId}`, {
      method: 'PATCH',
      headers: authHeaders(token),
      body: JSON.stringify(patch),
    }),

  archiveFarm: (token: string, farmId: number) =>
    request<{ farm_id: number; archived: true }>(`/api/admin/farms/${farmId}`, {
      method: 'DELETE',
      headers: authHeaders(token),
    }),

  createFarmMember: (token: string, farmId: number, input: FarmMemberCreateInput) =>
    request<{ member_id: number }>(`/api/admin/farms/${farmId}/members`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  updateFarmMember: (
    token: string, farmId: number, memberId: number, patch: FarmMemberUpdateInput,
  ) =>
    request<{ member_id: number; updated: true }>(
      `/api/admin/farms/${farmId}/members/${memberId}`,
      {
        method: 'PATCH',
        headers: authHeaders(token),
        body: JSON.stringify(patch),
      },
    ),

  archiveFarmMember: (token: string, farmId: number, memberId: number) =>
    request<{ member_id: number; archived: true }>(
      `/api/admin/farms/${farmId}/members/${memberId}`,
      {
        method: 'DELETE',
        headers: authHeaders(token),
      },
    ),

  // Farms the signed-in producer belongs to (S1; /api/farmer/* group per Pin 1).
  myFarms: (token: string) =>
    request<{ farms: MyFarm[] }>('/api/farmer/farms', {
      headers: authHeaders(token),
    }),

  // ----- Admin: documents (Build S2, Task #18) -----
  adminDocuments: (
    token: string,
    params?: { q?: string; type_code?: string; farm_id?: number; status?: string; scope_type?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.q)          qs.set('q', params.q);
    if (params?.type_code)  qs.set('type_code', params.type_code);
    if (params?.farm_id)    qs.set('farm_id', String(params.farm_id));
    if (params?.status)     qs.set('status', params.status);
    if (params?.scope_type) qs.set('scope_type', params.scope_type);
    const query = qs.toString();
    return request<{ documents: AdminDocument[]; kpis: DocumentKpis; types: DocumentTypeInfo[] }>(
      `/api/admin/documents${query ? `?${query}` : ''}`,
      { headers: authHeaders(token) },
    );
  },

  adminDocument: (token: string, documentId: string) =>
    request<AdminDocumentDetail>(`/api/admin/documents/${documentId}`, {
      headers: authHeaders(token),
    }),

  documentUploadUrl: (token: string, input: DocumentUploadUrlInput) =>
    request<DocumentUploadUrlResult>('/api/admin/documents/upload-url', {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  documentVersionUploadUrl: (
    token: string, documentId: string,
    input: Pick<DocumentUploadUrlInput, 'file_name' | 'mime_type' | 'byte_size' | 'sha256_base64'>,
  ) =>
    request<DocumentUploadUrlResult>(`/api/admin/documents/${documentId}/versions/upload-url`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  documentFinalize: (token: string, documentId: string, versionId: string) =>
    request<{ version_id: string; finalized?: boolean; already_finalized?: boolean; sha256?: string }>(
      `/api/admin/documents/${documentId}/finalize`,
      {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ version_id: versionId }),
      },
    ),

  updateDocument: (
    token: string, documentId: string,
    patch: Partial<Pick<AdminDocument, 'title' | 'scope_id' | 'farm_id' | 'status' | 'visibility' | 'sensitivity_level'>> & { scope_type?: DocumentScopeType },
  ) =>
    request<{ document_id: string; updated: true }>(`/api/admin/documents/${documentId}`, {
      method: 'PATCH',
      headers: authHeaders(token),
      body: JSON.stringify(patch),
    }),

  archiveDocument: (token: string, documentId: string) =>
    request<{ document_id: string; archived: true }>(`/api/admin/documents/${documentId}`, {
      method: 'DELETE',
      headers: authHeaders(token),
    }),

  shareDocument: (token: string, documentId: string, farmId: number) =>
    request<{ share_id: string }>(`/api/admin/documents/${documentId}/share`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ farm_id: farmId }),
    }),

  unshareDocument: (token: string, documentId: string, shareId: string) =>
    request<{ share_id: string; archived: true }>(`/api/admin/documents/${documentId}/share/${shareId}`, {
      method: 'DELETE',
      headers: authHeaders(token),
    }),

  requestDocumentApproval: (
    token: string, documentId: string,
    input: { version_id: string; role_label: 'UIF_REVIEWER' | 'PRODUCER'; requested_of_admin_id?: number | null; requested_of_member_id?: number | null },
  ) =>
    request<{ approval_id: string }>(`/api/admin/documents/${documentId}/approvals`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  actOnDocumentApproval: (
    token: string, approvalId: string,
    input: { status: 'approved' | 'changes_requested'; comment?: string | null },
  ) =>
    request<{ approval_id: string; status: string }>(`/api/admin/documents/approvals/${approvalId}`, {
      method: 'PATCH',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  adminDocumentDownloadUrl: (token: string, documentId: string, versionId?: string) =>
    request<{ download_url: string; expires_in: number }>(
      `/api/admin/documents/${documentId}/download-url${versionId ? `?version_id=${versionId}` : ''}`,
      { headers: authHeaders(token) },
    ),

  // ----- Farmer: shared documents (Build S2) -----
  farmerDocuments: (token: string) =>
    request<{ documents: FarmerDocument[] }>('/api/farmer/documents', {
      headers: authHeaders(token),
    }),

  farmerDocumentDownloadUrl: (token: string, documentId: string, disposition?: 'inline' | 'attachment') =>
    request<{ download_url: string; file_name: string; mime_type: string; expires_in: number }>(
      `/api/farmer/documents/${documentId}/download-url${disposition ? `?disposition=${disposition}` : ''}`,
      { headers: authHeaders(token) },
    ),

  // ----- Admin: signature envelopes (Build S3) -----
  adminEnvelopes: (token: string, params?: { status?: string; subject_id?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.subject_id) qs.set('subject_id', params.subject_id);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{ envelopes: AdminEnvelope[] }>(`/api/admin/envelopes${suffix}`, {
      headers: authHeaders(token),
    });
  },

  adminEnvelopeCreate: (token: string, input: EnvelopeCreateInput) =>
    request<{ envelope_id: string }>('/api/admin/envelopes', {
      method: 'POST', headers: authHeaders(token), body: JSON.stringify(input),
    }),

  adminEnvelopeDetail: (token: string, envelopeId: string) =>
    request<AdminEnvelopeDetail>(`/api/admin/envelopes/${envelopeId}`, {
      headers: authHeaders(token),
    }),

  adminEnvelopePartyAdd: (token: string, envelopeId: string, input: EnvelopePartyInput) =>
    request<{ party_id: string }>(`/api/admin/envelopes/${envelopeId}/parties`, {
      method: 'POST', headers: authHeaders(token), body: JSON.stringify(input),
    }),

  adminEnvelopePartyDelete: (token: string, envelopeId: string, partyId: string) =>
    request<{ deleted: boolean }>(`/api/admin/envelopes/${envelopeId}/parties/${partyId}`, {
      method: 'DELETE', headers: authHeaders(token),
    }),

  adminEnvelopeSend: (token: string, envelopeId: string) =>
    request<{ sent: boolean }>(`/api/admin/envelopes/${envelopeId}/send`, {
      method: 'POST', headers: authHeaders(token),
    }),

  adminEnvelopeVoid: (token: string, envelopeId: string) =>
    request<{ voided: boolean }>(`/api/admin/envelopes/${envelopeId}/void`, {
      method: 'POST', headers: authHeaders(token),
    }),

  adminEnvelopeConsent: (token: string, envelopeId: string) =>
    request<{ consent_recorded: boolean }>(`/api/admin/envelopes/${envelopeId}/consent`, {
      method: 'POST', headers: authHeaders(token),
    }),

  adminEnvelopeSign: (token: string, envelopeId: string, input: SignInput) =>
    request<{ signed: boolean; envelope_status: string }>(`/api/admin/envelopes/${envelopeId}/sign`, {
      method: 'POST', headers: authHeaders(token), body: JSON.stringify(input),
    }),

  adminEnvelopeCertificateUrl: (token: string, envelopeId: string) =>
    request<{ download_url: string; file_name: string; expires_in: number }>(
      `/api/admin/envelopes/${envelopeId}/certificate-url`,
      { headers: authHeaders(token) },
    ),

  // ----- Admin: legal agreements (Build S3) -----
  adminAgreements: (token: string, params?: { status?: string }) => {
    const suffix = params?.status ? `?status=${encodeURIComponent(params.status)}` : '';
    return request<{ agreements: AdminAgreement[] }>(`/api/admin/agreements${suffix}`, {
      headers: authHeaders(token),
    });
  },

  adminAgreementCreate: (token: string, input: AgreementCreateInput) =>
    request<{ agreement_id: string }>('/api/admin/agreements', {
      method: 'POST', headers: authHeaders(token), body: JSON.stringify(input),
    }),

  adminAgreementDetail: (token: string, agreementId: string) =>
    request<AdminAgreementDetail>(`/api/admin/agreements/${agreementId}`, {
      headers: authHeaders(token),
    }),

  adminAgreementPatch: (token: string, agreementId: string, patch: AgreementPatchInput) =>
    request<{ updated: boolean }>(`/api/admin/agreements/${agreementId}`, {
      method: 'PATCH', headers: authHeaders(token), body: JSON.stringify(patch),
    }),

  adminAgreementAmend: (token: string, agreementId: string, input: AgreementAmendInput) =>
    request<{ envelope_id: string }>(`/api/admin/agreements/${agreementId}/amend`, {
      method: 'POST', headers: authHeaders(token), body: JSON.stringify(input),
    }),

  adminExhibitPublish: (token: string, agreementId: string, input: ExhibitPublishInput) =>
    request<{ exhibit_id: string; exhibit_version: number }>(
      `/api/admin/agreements/${agreementId}/exhibits`,
      { method: 'POST', headers: authHeaders(token), body: JSON.stringify(input) },
    ),

  // ----- Farmer: signing surface (Build S3) -----
  farmerEnvelopes: (token: string) =>
    request<{ envelopes: FarmerEnvelope[] }>('/api/farmer/envelopes', {
      headers: authHeaders(token),
    }),

  farmerEnvelopeDetail: (token: string, envelopeId: string) =>
    request<FarmerEnvelopeDetail>(`/api/farmer/envelopes/${envelopeId}`, {
      headers: authHeaders(token),
    }),

  farmerEnvelopeConsent: (token: string, envelopeId: string) =>
    request<{ consent_recorded: boolean }>(`/api/farmer/envelopes/${envelopeId}/consent`, {
      method: 'POST', headers: authHeaders(token),
    }),

  farmerEnvelopeSign: (token: string, envelopeId: string, input: SignInput) =>
    request<{ signed: boolean; envelope_status: string }>(`/api/farmer/envelopes/${envelopeId}/sign`, {
      method: 'POST', headers: authHeaders(token), body: JSON.stringify(input),
    }),

  farmerEnvelopeDecline: (token: string, envelopeId: string, reason: string) =>
    request<{ declined: boolean }>(`/api/farmer/envelopes/${envelopeId}/decline`, {
      method: 'POST', headers: authHeaders(token), body: JSON.stringify({ reason }),
    }),

  farmerEnvelopePaperPreference: (token: string, envelopeId: string) =>
    request<{ recorded: boolean }>(`/api/farmer/envelopes/${envelopeId}/paper-preference`, {
      method: 'POST', headers: authHeaders(token),
    }),

  // ----- External: magic-link signing (Build S3; token in BODY post-load) -----
  // Task #44 Phase B: public tokenized stipend-address collection. No auth —
  // the token is the credential. Every failure is an identical 404.
  attendeeInfoLookup: (token: string) =>
    request<AttendeeInfoLookup>(`/api/attendee-info/${encodeURIComponent(token)}`),

  attendeeInfoSubmit: (token: string, address: AttendeeAddress) =>
    request<{ submitted: boolean; address_collected_at: string }>('/api/attendee-info/submit', {
      method: 'POST', body: JSON.stringify({ token, address }),
    }),

  externalSignDetail: (linkToken: string) =>
    request<ExternalSignDetail>(`/api/sign/${encodeURIComponent(linkToken)}`),

  externalSignConsent: (linkToken: string) =>
    request<{ consent_recorded: boolean }>('/api/sign/consent', {
      method: 'POST', body: JSON.stringify({ token: linkToken }),
    }),

  externalSignSign: (linkToken: string, input: SignInput) =>
    request<{ signed: boolean; envelope_status: string }>('/api/sign/sign', {
      method: 'POST', body: JSON.stringify({ token: linkToken, ...input }),
    }),

  externalSignDecline: (linkToken: string, reason: string) =>
    request<{ declined: boolean }>('/api/sign/decline', {
      method: 'POST', body: JSON.stringify({ token: linkToken, reason }),
    }),

  externalSignPaperPreference: (linkToken: string) =>
    request<{ recorded: boolean }>('/api/sign/paper-preference', {
      method: 'POST', body: JSON.stringify({ token: linkToken }),
    }),

  // ----- Public: email subscription signup + confirm + unsub (Task #9) -----
  subscribe: (input: SubscriberInput) =>
    request<{ ok: boolean; message: string; subscriber_id?: number }>('/api/subscribers', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  confirmSubscription: (token: string) =>
    request<{ confirmed: boolean; email?: string; message?: string }>(
      `/api/subscribers/confirm/${encodeURIComponent(token)}`,
    ),

  unsubscribe: (token: string) =>
    request<{ unsubscribed: boolean; email: string }>(
      `/api/subscribers/unsubscribe/${encodeURIComponent(token)}`,
    ),

  // ----- UIF only: subscriber admin (Task #9) -----
  adminSubscribers: (token: string, status?: SubscriberStatus) => {
    const qs = status ? `?status=${status}` : '';
    return request<{
      subscribers: Subscriber[];
      counts: Array<{ status: SubscriberStatus; count: number }>;
    }>(`/api/admin/subscribers${qs}`, { headers: authHeaders(token) });
  },

  archiveSubscriber: (token: string, id: number) =>
    request<{ subscriber_id: number; archived: true }>(`/api/admin/subscribers/${id}`, {
      method: 'DELETE',
      headers: authHeaders(token),
    }),

  sendSubscriberBlast: (token: string, input: SubscriberBlastInput) =>
    request<{ sent: number; failed: number; total?: number }>('/api/admin/subscribers/email', {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  // ----- UIF only: cross-event budget reports (Task #8) -----
  budgetReport: (
    token: string,
    params?: { from?: string; to?: string; includeDrafts?: boolean },
  ) => {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to)   qs.set('to', params.to);
    if (params?.includeDrafts) qs.set('include_drafts', 'true');
    const query = qs.toString();
    return request<BudgetReport>(
      `/api/admin/reports/budget${query ? `?${query}` : ''}`,
      { headers: authHeaders(token) },
    );
  },

  // ----- UIF only: admin CRUD over uif_site.event_tag (Task #5) -----
  adminTags: (token: string) =>
    request<{ tags: AdminTag[] }>('/api/admin/tags', {
      headers: authHeaders(token),
    }),

  createTag: (token: string, input: TagInput) =>
    request<{ tag_id: number }>('/api/admin/tags', {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  updateTag: (token: string, tagId: number, patch: TagUpdateInput) =>
    request<{ tag_id: number; updated: boolean }>(`/api/admin/tags/${tagId}`, {
      method: 'PATCH',
      headers: authHeaders(token),
      body: JSON.stringify(patch),
    }),

  archiveTag: (token: string, tagId: number) =>
    request<{ tag_id: number; archived: true }>(`/api/admin/tags/${tagId}`, {
      method: 'DELETE',
      headers: authHeaders(token),
    }),

  // ----- UIF only: admin CRUD over uif_site.shared_resource (Task #3) -----
  // Returns ALL resources including archived ones so the panel can re-activate.
  adminResources: (token: string) =>
    request<{ resources: AdminResource[] }>('/api/admin/resources', {
      headers: authHeaders(token),
    }),

  createResource: (token: string, input: ResourceInput) =>
    request<{ resource_id: number }>('/api/admin/resources', {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  updateResource: (token: string, resourceId: number, patch: ResourceUpdateInput) =>
    request<{ resource_id: number; updated: boolean }>(`/api/admin/resources/${resourceId}`, {
      method: 'PATCH',
      headers: authHeaders(token),
      body: JSON.stringify(patch),
    }),

  archiveResource: (token: string, resourceId: number) =>
    request<{ resource_id: number; archived: true }>(`/api/admin/resources/${resourceId}`, {
      method: 'DELETE',
      headers: authHeaders(token),
    }),

  register: (input: RegistrationInput) =>
    request<RegistrationResult>('/api/registrations', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // ----- Auth -----
  adminLogin: (input: AdminLoginInput) =>
    request<AdminLoginResult>('/api/admin/login', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // ----- UIF: registrations + AMP inquiries -----
  adminRegistrations: (token: string) =>
    request<{ registrations: AdminRegistration[] }>('/api/admin/registrations', {
      headers: authHeaders(token),
    }),

  // Task #51: edit registrant contact info (name/email/phone/seats/notes).
  adminUpdateRegistrant: (
    token: string,
    attendeeId: number,
    patch: Partial<{ full_name: string; email: string; phone: string | null; attendee_count: number; special_requests: string | null }>,
  ) =>
    request<{ attendee_id: number; updated: boolean }>(
      `/api/admin/registrations/${attendeeId}`,
      { method: 'PATCH', headers: authHeaders(token), body: JSON.stringify(patch) },
    ),

  adminUpdateRegistrationStatus: (token: string, attendeeId: number, status: string) =>
    request<{ attendee_id: number; registration_status: string }>(
      `/api/admin/registrations/${attendeeId}/status`,
      {
        method: 'PATCH',
        headers: authHeaders(token),
        body: JSON.stringify({ registration_status: status }),
      },
    ),

  // Task #48: bulk email to event registrants.
  sendEventEmailBlast: (token: string, calendarEntryId: number, input: EmailBlastInput) =>
    request<EmailBlastResult>(`/api/admin/events/${calendarEntryId}/email`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  // Task #48: public attendance confirmation by UUID token.
  confirmRegistration: (token: string) =>
    request<ConfirmRegistrationResult>(`/api/registrations/confirm/${token}`),

  // Task #2: UIF password reset.
  requestPasswordReset: (email: string) =>
    request<{ ok: boolean; message: string }>(`/api/admin/password-reset/request`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  confirmPasswordReset: (token: string, password: string) =>
    request<{ ok: boolean; username: string }>(`/api/admin/password-reset/confirm`, {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  // Task #52: public sign-in flow.
  signinLookup: (input: { event_id: number; day_index: number; email: string }) =>
    request<SignInLookupResult>(`/api/signin/lookup`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  signinSubmit: (input: SignInSubmitInput) =>
    request<{ ok: boolean }>(`/api/signin/submit`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // Task #49: per-day attendance.
  listAttendance: (token: string, calendarEntryId: number) =>
    request<{ attendance: AttendanceRow[] }>(
      `/api/admin/events/${calendarEntryId}/attendance`,
      { headers: authHeaders(token) },
    ),

  // Task #44: printable attendance report. Keyed by event_id — NOT the
  // calendar_entry_id that listAttendance above takes.
  attendanceReport: (token: string, eventId: number) =>
    request<AttendanceReport>(
      `/api/admin/events/${eventId}/attendance-report`,
      { headers: authHeaders(token) },
    ),

  markAttendance: (token: string, attendeeId: number, input: AttendanceMarkInput) =>
    request<{ attendance_synced: boolean; present_days?: number; total_days?: number }>(
      `/api/admin/attendees/${attendeeId}/attendance`,
      {
        method: 'PATCH',
        headers: authHeaders(token),
        body: JSON.stringify(input),
      },
    ),

  // Task #49: event evidence attachments.
  listEventAttachments: (token: string, calendarEntryId: number) =>
    request<{ attachments: EventAttachment[] }>(
      `/api/admin/events/${calendarEntryId}/attachments`,
      { headers: authHeaders(token) },
    ),

  createEventAttachment: (token: string, calendarEntryId: number, input: EventAttachmentCreateInput) =>
    request<{ attachment_id: number }>(
      `/api/admin/events/${calendarEntryId}/attachments`,
      {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(input),
      },
    ),

  archiveEventAttachment: (token: string, calendarEntryId: number, attachmentId: number) =>
    request<{ attachment_id: number; archived: boolean }>(
      `/api/admin/events/${calendarEntryId}/attachments/${attachmentId}`,
      { method: 'DELETE', headers: authHeaders(token) },
    ),

  // Task #50: admin guest CRUD on a registration.
  addGuest: (token: string, attendeeId: number, fullName: string) =>
    request<{ guest_id: number }>(`/api/admin/attendees/${attendeeId}/guests`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ full_name: fullName }),
    }),

  updateGuest: (token: string, attendeeId: number, guestId: number, fullName: string) =>
    request<{ guest_id: number; updated: boolean }>(`/api/admin/attendees/${attendeeId}/guests/${guestId}`, {
      method: 'PATCH',
      headers: authHeaders(token),
      body: JSON.stringify({ full_name: fullName }),
    }),

  archiveGuest: (token: string, attendeeId: number, guestId: number) =>
    request<{ guest_id: number; archived: boolean }>(`/api/admin/attendees/${attendeeId}/guests/${guestId}`, {
      method: 'DELETE',
      headers: authHeaders(token),
    }),

  adminAmpInquiries: (token: string) =>
    request<{ inquiries: any[]; limit: number; offset: number }>('/api/admin/amp-inquiries', {
      headers: authHeaders(token),
    }),

  // ----- UIF: calendar event CRUD (Phase 2) -----
  createEvent: (token: string, input: EventCreateInput) =>
    request<EventCreateResult>('/api/admin/events', {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  updateEvent: (token: string, calendarEntryId: number, patch: EventUpdateInput) =>
    request<{ calendar_entry_id: number; updated: true }>(
      `/api/admin/events/${calendarEntryId}`,
      {
        method: 'PATCH',
        headers: authHeaders(token),
        body: JSON.stringify(patch),
      },
    ),

  archiveEvent: (token: string, calendarEntryId: number) =>
    request<{ calendar_entry_id: number; archived: true }>(
      `/api/admin/events/${calendarEntryId}`,
      {
        method: 'DELETE',
        headers: authHeaders(token),
      },
    ),

  // ----- UIF + AGC: shared room bookings (Phase 2) -----
  listBookings: (token: string, opts?: { from?: string; to?: string }) => {
    const params = new URLSearchParams();
    if (opts?.from) params.set('from', opts.from);
    if (opts?.to)   params.set('to',   opts.to);
    const qs = params.toString() ? `?${params}` : '';
    return request<BookingListResponse>(`/api/calendar/bookings${qs}`, {
      headers: authHeaders(token),
    });
  },

  createBooking: (token: string, input: BookingCreateInput) =>
    request<{ booking_id: number }>('/api/calendar/bookings', {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(input),
    }),

  updateBooking: (token: string, bookingId: number, patch: BookingUpdateInput) =>
    request<{ booking_id: number; updated: true }>(
      `/api/calendar/bookings/${bookingId}`,
      {
        method: 'PATCH',
        headers: authHeaders(token),
        body: JSON.stringify(patch),
      },
    ),

  archiveBooking: (token: string, bookingId: number) =>
    request<{ booking_id: number; archived: true }>(
      `/api/calendar/bookings/${bookingId}`,
      {
        method: 'DELETE',
        headers: authHeaders(token),
      },
    ),

  // ----- UIF only: event budget (Phase 2) -----
  budget: (token: string, calendarEntryId: number) =>
    request<BudgetResponse>(`/api/admin/events/${calendarEntryId}/budget`, {
      headers: authHeaders(token),
    }),

  createBudgetLine: (token: string, calendarEntryId: number, line: BudgetLineCreateInput) =>
    request<{ line_id: number }>(
      `/api/admin/events/${calendarEntryId}/budget/lines`,
      {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(line),
      },
    ),

  updateBudgetLine: (
    token: string,
    calendarEntryId: number,
    lineId: number,
    patch: BudgetLineUpdateInput,
  ) =>
    request<{ line_id: number; updated: true }>(
      `/api/admin/events/${calendarEntryId}/budget/lines/${lineId}`,
      {
        method: 'PATCH',
        headers: authHeaders(token),
        body: JSON.stringify(patch),
      },
    ),

  archiveBudgetLine: (token: string, calendarEntryId: number, lineId: number) =>
    request<{ line_id: number; archived: true }>(
      `/api/admin/events/${calendarEntryId}/budget/lines/${lineId}`,
      {
        method: 'DELETE',
        headers: authHeaders(token),
      },
    ),

  // ----- Build S4: programs + enrollment (Task #25) -----

  adminPrograms: (token: string) =>
    request<{ programs: AdminProgram[] }>('/api/admin/programs', { headers: authHeaders(token) }),

  adminProgramPatch: (token: string, programId: string, patch: Partial<Pick<AdminProgram, 'program_name' | 'program_category' | 'funding_agency' | 'governing_regulation_ref' | 'status'>>) =>
    request<{ program_id: string; updated: true }>(`/api/admin/programs/${programId}`, {
      method: 'PATCH', headers: authHeaders(token), body: JSON.stringify(patch),
    }),

  adminProgramYearCreate: (token: string, programId: string, input: { year_no: number; enrollment_open_date?: string | null; enrollment_close_date?: string | null; template_agreement_version?: string | null; annual_budget?: number | null; notes?: string | null }) =>
    request<{ program_year: AdminProgramYear }>(`/api/admin/programs/${programId}/years`, {
      method: 'POST', headers: authHeaders(token), body: JSON.stringify(input),
    }),

  adminProgramYearPatch: (token: string, programYearId: string, patch: { enrollment_open_date?: string | null; enrollment_close_date?: string | null; template_agreement_version?: string | null; annual_budget?: number | null; notes?: string | null; is_current?: boolean }) =>
    request<{ program_year_id: string; updated: true }>(`/api/admin/program-years/${programYearId}`, {
      method: 'PATCH', headers: authHeaders(token), body: JSON.stringify(patch),
    }),

  adminEnrollments: (token: string, filters?: { program_year_id?: string; status?: string; farm_id?: number }) => {
    const params = new URLSearchParams();
    if (filters?.program_year_id) params.set('program_year_id', filters.program_year_id);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.farm_id != null) params.set('farm_id', String(filters.farm_id));
    const qs = params.toString();
    return request<{ enrollments: EnrollmentSummary[] }>(`/api/admin/enrollments${qs ? `?${qs}` : ''}`, {
      headers: authHeaders(token),
    });
  },

  adminEnrollmentCreate: (token: string, input: { farm_id: number; program_year_id: string; cohort_tag?: string }) =>
    request<{ enrollment: EnrollmentSummary }>('/api/admin/enrollments', {
      method: 'POST', headers: authHeaders(token), body: JSON.stringify(input),
    }),

  adminEnrollmentDetail: (token: string, enrollmentId: string) =>
    request<AdminEnrollmentDetail>(`/api/admin/enrollments/${enrollmentId}`, { headers: authHeaders(token) }),

  adminEnrollmentTransition: (token: string, enrollmentId: string, toStatus: EnrollmentStatus, reason?: string | null) =>
    request<{ enrollment: EnrollmentSummary }>(`/api/admin/enrollments/${enrollmentId}/transition`, {
      method: 'POST', headers: authHeaders(token),
      body: JSON.stringify({ to_status: toStatus, reason: reason ?? null }),
    }),

  adminEnrollmentAttachAgreement: (token: string, enrollmentId: string, agreementId: string) =>
    request<{ enrollment_id: string; agreement_id: string; attached: true }>(`/api/admin/enrollments/${enrollmentId}/agreement`, {
      method: 'POST', headers: authHeaders(token), body: JSON.stringify({ agreement_id: agreementId }),
    }),

  adminEnrollmentRecompute: (token: string, enrollmentId: string) =>
    request<{ snapshot: EligibilitySnapshot }>(`/api/admin/enrollments/${enrollmentId}/recompute`, {
      method: 'POST', headers: authHeaders(token),
    }),

  // Build S6b (Task #37): record externally-executed AMP agreements.
  adminEnrollmentRecordExternal: (token: string, enrollmentId: string, payload: RecordExternalAgreementPayload) =>
    request<{ enrollment: EnrollmentSummary }>(`/api/admin/enrollments/${enrollmentId}/record-external-agreement`, {
      method: 'POST', headers: authHeaders(token), body: JSON.stringify(payload),
    }),

  adminEnrollmentRecordExternalBatch: (token: string, rows: RecordExternalBatchRow[]) =>
    request<{ results: RecordExternalBatchResult[] }>(`/api/admin/enrollments/record-external-agreement-batch`, {
      method: 'POST', headers: authHeaders(token), body: JSON.stringify({ rows }),
    }),

  adminEnrollmentVocOnFile: (token: string, enrollmentId: string) =>
    request<VocOnFileResult>(`/api/admin/enrollments/${enrollmentId}/voc-on-file`, { headers: authHeaders(token) }),

  farmerEnrollments: (token: string) =>
    request<{ enrollments: FarmerEnrollment[] }>('/api/farmer/enrollments', { headers: authHeaders(token) }),

  farmerEnrollmentDetail: (token: string, enrollmentId: string) =>
    request<{ enrollment: FarmerEnrollment; snapshot: EligibilitySnapshot | null }>(`/api/farmer/enrollments/${enrollmentId}`, {
      headers: authHeaders(token),
    }),

  // Build S5: tracks + milestones (Task #22)
  adminTracks: (token: string, filters?: { enrollment_id?: string; status?: string }) => {
    const p = new URLSearchParams();
    if (filters?.enrollment_id) p.set('enrollment_id', filters.enrollment_id);
    if (filters?.status) p.set('status', filters.status);
    const qs = p.toString();
    return request<{ track_plans: TrackPlanSummary[] }>(`/api/admin/tracks${qs ? `?${qs}` : ''}`, { headers: authHeaders(token) });
  },
  adminTrackDetail: (token: string, trackPlanId: string) =>
    request<AdminTrackDetail>(`/api/admin/tracks/${trackPlanId}`, { headers: authHeaders(token) }),
  adminTrackCreate: (token: string, input: { enrollment_id: string; track_code: TrackCode; plan_summary?: string | null; plan_document_id?: string | null }) =>
    request<{ track_plan: TrackPlan }>('/api/admin/tracks', { method: 'POST', headers: authHeaders(token), body: JSON.stringify(input) }),
  adminTrackPatch: (token: string, trackPlanId: string, input: { plan_summary?: string | null; plan_document_id?: string | null; status?: TrackPlanStatus }) =>
    request<{ track_plan_id: string; updated: true }>(`/api/admin/tracks/${trackPlanId}`, { method: 'PATCH', headers: authHeaders(token), body: JSON.stringify(input) }),
  adminMilestoneTransition: (token: string, milestoneId: string, input: { to_status: MilestoneStatus; reason?: string | null; not_applicable_reason?: string | null; waived_reason?: string | null }) =>
    request<{ milestone: FarmMilestoneCore }>(`/api/admin/milestones/${milestoneId}/transition`, { method: 'POST', headers: authHeaders(token), body: JSON.stringify(input) }),
  adminMilestoneAttachEvidence: (token: string, milestoneId: string, input: { evidence_type: string; document_id?: string | null; agreement_id?: string | null; envelope_id?: string | null; notes?: string | null }) =>
    request<{ evidence: MilestoneEvidence }>(`/api/admin/milestones/${milestoneId}/evidence`, { method: 'POST', headers: authHeaders(token), body: JSON.stringify(input) }),
  adminMilestoneDefs: (token: string) =>
    request<{ milestone_defs: MilestoneDef[] }>('/api/admin/milestone-defs', { headers: authHeaders(token) }),
  adminMilestoneDefPatch: (token: string, defId: string, input: { label?: string; description?: string | null; exit_criteria?: string | null; evidence_required?: string[] | null; requires_cpa52?: boolean; is_draft?: boolean; sort_order?: number }) =>
    request<{ milestone_def_id: string; updated: true }>(`/api/admin/milestone-defs/${defId}`, { method: 'PATCH', headers: authHeaders(token), body: JSON.stringify(input) }),
  farmerTracks: (token: string) =>
    request<{ track_plans: TrackPlanSummary[] }>('/api/farmer/tracks', { headers: authHeaders(token) }),
  farmerTrackDetail: (token: string, trackPlanId: string) =>
    request<FarmerTrackDetail>(`/api/farmer/tracks/${trackPlanId}`, { headers: authHeaders(token) }),

  // Build S6 — practices + CPA-52 (Task #26)
  adminDocumentTypes: (token: string) =>
    request<{ document_types: DocumentTypeVocab[] }>('/api/admin/document-types', { headers: authHeaders(token) }),
  adminPracticeDefs: (token: string) =>
    request<{ practice_defs: PracticeDef[] }>('/api/admin/practice-defs', { headers: authHeaders(token) }),
  adminPracticeDefPatch: (token: string, defId: string, input: Partial<Pick<PracticeDef, 'practice_name' | 'category' | 'unit_of_measure' | 'default_lifespan_years' | 'description' | 'is_active' | 'sort_order'>>) =>
    request<{ practice_def_id: string; updated: true }>(`/api/admin/practice-defs/${defId}`, { method: 'PATCH', headers: authHeaders(token), body: JSON.stringify(input) }),
  adminPractices: (token: string, params?: { farm_id?: number; status?: PracticeStatus }) => {
    const qs = new URLSearchParams();
    if (params?.farm_id != null) qs.set('farm_id', String(params.farm_id));
    if (params?.status) qs.set('status', params.status);
    const q = qs.toString();
    return request<{ practices: PracticeInstance[] }>(`/api/admin/practices${q ? `?${q}` : ''}`, { headers: authHeaders(token) });
  },
  adminPracticeCreate: (token: string, input: { subfield_id: number; farm_id: number; practice_def_id: string; enrollment_id?: string | null; program_year_id?: string | null; track_code?: TrackCode | null; status?: 'planned' | 'historic'; planned_amount?: number | null; planned_at?: string | null; notes?: string | null }) =>
    request<{ practice: PracticeInstance }>('/api/admin/practices', { method: 'POST', headers: authHeaders(token), body: JSON.stringify(input) }),
  adminPracticeDetail: (token: string, id: string) =>
    request<{ practice: PracticeInstance; allowed_transitions: PracticeStatus[] }>(`/api/admin/practices/${id}`, { headers: authHeaders(token) }),
  adminPracticePatch: (token: string, id: string, input: { track_code?: TrackCode | null; enrollment_id?: string | null; program_year_id?: string | null; planned_amount?: number | null; implemented_amount?: number | null; planned_at?: string | null; implemented_at?: string | null; notes?: string | null }) =>
    request<{ practice_instance_id: string; updated: true }>(`/api/admin/practices/${id}`, { method: 'PATCH', headers: authHeaders(token), body: JSON.stringify(input) }),
  adminPracticeTransition: (token: string, id: string, input: { to_status: PracticeStatus; change_reason?: string | null; implemented_at?: string | null; implemented_amount?: number | null; verified_at?: string | null; verified_by?: number | null }) =>
    request<{ practice: PracticeInstance }>(`/api/admin/practices/${id}/transition`, { method: 'POST', headers: authHeaders(token), body: JSON.stringify(input) }),
  adminCpa52List: (token: string, params?: { farm_id?: number; status?: Cpa52Status }) => {
    const qs = new URLSearchParams();
    if (params?.farm_id != null) qs.set('farm_id', String(params.farm_id));
    if (params?.status) qs.set('status', params.status);
    const q = qs.toString();
    return request<{ submissions: Cpa52Submission[] }>(`/api/admin/cpa52${q ? `?${q}` : ''}`, { headers: authHeaders(token) });
  },
  adminCpa52Create: (token: string, input: { farm_id: number; enrollment_id: string; practice_instance_ids: string[]; submission_number?: string | null; notes?: string | null }) =>
    request<{ submission: Cpa52Submission }>('/api/admin/cpa52', { method: 'POST', headers: authHeaders(token), body: JSON.stringify(input) }),
  adminCpa52Detail: (token: string, id: string) =>
    request<{ submission: Cpa52Submission; allowed_transitions: Cpa52Status[]; practices: Cpa52SubmissionPractice[] }>(`/api/admin/cpa52/${id}`, { headers: authHeaders(token) }),
  adminCpa52Patch: (token: string, id: string, input: { submission_number?: string | null; notes?: string | null; envelope_id?: string }) =>
    request<{ cpa52_submission_id: string; updated: true }>(`/api/admin/cpa52/${id}`, { method: 'PATCH', headers: authHeaders(token), body: JSON.stringify(input) }),
  adminCpa52Transition: (token: string, id: string, input: { to_status: Cpa52Status; envelope_id?: string | null; executed_document_id?: string | null; submission_number?: string | null; effective_date?: string | null; notes?: string | null }) =>
    request<{ submission: Cpa52Submission }>(`/api/admin/cpa52/${id}/transition`, { method: 'POST', headers: authHeaders(token), body: JSON.stringify(input) }),
  farmerPractices: (token: string) =>
    request<{ practices: PracticeInstance[] }>('/api/farmer/practices', { headers: authHeaders(token) }),
  farmerPracticeDetail: (token: string, id: string) =>
    request<{ practice: PracticeInstance }>(`/api/farmer/practices/${id}`, { headers: authHeaders(token) }),
};
